import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import type { BoardroomRecipe } from '../../types';

/**
 * Start from a shape that worked before.
 *
 * SUGGESTED, NOT SEARCHED. Nobody opening a room remembers they saved a shape
 * six weeks ago, so the list ranks itself against what has just been typed —
 * a room about running shoes surfaces the baggy-clothes recipe without its
 * owner having to think of it.
 *
 * What a card promises is the SHAPE: the tables it sets up, the seats it puts
 * in the room, how many questions it starts with. Never its findings, which do
 * not travel — see `jcseo/boardroom/recipes.py`.
 */
export default function RecipePicker({
  subject,
  value,
  onChange,
}: {
  /** What the new room is about, as typed so far. */
  subject: string;
  /** The chosen recipe id, or null for a blank room. */
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  const [recipes, setRecipes] = useState<BoardroomRecipe[] | null>(null);

  // Re-ranked as they type, but only once they have typed something worth
  // ranking against — a two-word subject matches everything equally.
  useEffect(() => {
    let alive = true;
    const text = subject.trim();
    const t = setTimeout(() => {
      api
        .listBoardroomRecipes(text.split(/\s+/).length >= 3 ? text : undefined, 4)
        .then(rows => {
          if (alive) setRecipes(rows);
        })
        .catch(() => {
          if (alive) setRecipes([]);
        });
    }, 400);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [subject]);

  if (!recipes?.length) return null;

  return (
    <div className="mt-3" data-testid="recipe-picker">
      <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
        Start from a room you liked - it sets up the same tables, seats and first
        questions, and proposes what it compared on for you to re-word.
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onChange(null)}
          className={`rounded-md border px-2 py-1 text-xs ${
            value === null
              ? 'border-indigo-400 bg-indigo-50 text-indigo-800 dark:border-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-200'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          Blank room
        </button>
        {recipes.map(r => (
          <button
            key={r.id}
            type="button"
            onClick={() => onChange(value === r.id ? null : r.id)}
            title={[
              r.description,
              r.subject && `From: ${r.subject}`,
              r.deliverables.length && `${r.deliverables.length} tables`,
              r.seats.length && `${r.seats.length} seats`,
              r.questions.length && `${r.questions.length} starting questions`,
            ]
              .filter(Boolean)
              .join('\n')}
            className={`rounded-md border px-2 py-1 text-left text-xs ${
              value === r.id
                ? 'border-indigo-400 bg-indigo-50 text-indigo-800 dark:border-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-200'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <div className="font-medium">{r.name}</div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">
              {[
                r.family || null,
                r.deliverables.length ? `${r.deliverables.length} tables` : null,
                r.seats.length ? `${r.seats.length} seats` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
